import { notifyOnError } from '~/rage/decorators';
import { IObservable, Subject } from '~/rage/utils';
import { IPlayerCharacter, PlayerCharacterPreview } from '~/shared';
import { GameConfigService } from '../GameConfigService';
import { RpcService } from '../RpcService';
import { VarsService } from '../VarsService';
import { ICharacterSelectionService } from './characterSelectionServiceTypes';

export class CharacterSelectionService implements ICharacterSelectionService {
	private pricePerCharacter = new Subject<number[]>([]);
	private maxCharacters = new Subject<number>(0);

	constructor(
		private readonly vars: VarsService,
		private readonly rpc: RpcService,
		private readonly gameConfig: GameConfigService,
	) {
		this.getCharacters().subscribe((characters) => {
			if (!characters) return;
			this.handleCharactersUpdate(characters);
		});

		this.gameConfig.getConfigs().subscribe(() => {
			if (!this.gameConfig.configs.gameplay) {
				return;
			}

			this.pricePerCharacter.next(
				this.gameConfig.configs.gameplay.pricesPerCharacter,
			);
			this.maxCharacters.next(
				this.gameConfig.configs.gameplay.maxCharactersCount,
			);
		});
	}

	private async handleCharactersUpdate(
		characters: Array<IPlayerCharacter | null>,
	): Promise<void> {
		const previewCharacters = characters.map((character) => {
			if (!character) {
				return null;
			}

			const { appearance, clothes, tattoos } = character;
			return { appearance, clothes, tattoos };
		});

		await this.setCharactersToPreview(previewCharacters);
	}

	public getCharacters(): IObservable<Array<IPlayerCharacter | null> | null> {
		return this.vars.get('characterSelection:characters', null);
	}

	public getPricePerCharacter(): IObservable<number[]> {
		return this.pricePerCharacter;
	}

	public getMaxCharacters(): IObservable<number> {
		return this.maxCharacters;
	}

	@notifyOnError
	public async createCharacter(characterSlotIndex: number): Promise<void> {
		return await this.rpc.invokeClient('characterSelection:createCharacter', {
			characterSlotIndex,
		});
	}

	@notifyOnError
	public async selectCharacter(characterId: string): Promise<void> {
		return await this.rpc.invokeClient('characterSelection:selectCharacter', {
			characterId,
		});
	}

	@notifyOnError
	public async setCurrentIndex(index: number): Promise<void> {
		return await this.rpc.invokeClient('characterSelection:setCurrentIndex', {
			index,
		});
	}

	private async setCharactersToPreview(
		characters: PlayerCharacterPreview[],
	): Promise<void> {
		return await this.rpc.invokeClient(
			'characterSelection:setCharactersToPreview',
			{
				characters,
			},
		);
	}

	public getAccountLogin(): IObservable<string> {
		return this.vars.get('characterSelection:accountLogin', '');
	}
}
